import type { StripePriceId } from '@editor/types/Pricing';
import { SubscriptionPlanType } from '@repo/subscription/api';
import { useSubscriptionStore } from '@repo/subscription/client';
import { StateCreator } from 'zustand';

import { getAddons } from '../../global/pricingConfig';

import { PlanSelectionStore } from './types';

const getUpgradePlan = (
  currentPlan: SubscriptionPlanType | undefined
): SubscriptionPlanType => {
  return !currentPlan ? SubscriptionPlanType.PRO : SubscriptionPlanType.EXPERT;
};

const initialState = {
  plan: SubscriptionPlanType.PRO,
  yearly: true,
  // TODO: Addon is no longer supported, cleanup later. https://app.clickup.com/t/86bz8c54t
  expertAddonPriceId: null,
  quantity: 1,
  upgrade: false,
  upgradeToExpertPlan: false,
  promoCode: null,
  disabled: false,
} as const;

export const planSelectionStateCreator: StateCreator<PlanSelectionStore> = (
  set,
  get
) => ({
  ...initialState,
  actions: {
    reset: (): void => {
      set(initialState);
    },
    setPlan: (plan): void => {
      set({ plan });

      if (plan !== SubscriptionPlanType.EXPERT) {
        set({ expertAddonPriceId: null });
      }
    },
    setYearly: (yearly): void => {
      let expertAddonPriceId = get().expertAddonPriceId;

      // If an expert addon is currently selected, select a matching addon
      // from the other payment interval price set.
      const currentAddons = getAddons();
      const newAddons = getAddons();

      const currentAddonIndex = currentAddons.findIndex(
        ({ stripeId }) => stripeId === expertAddonPriceId
      );

      expertAddonPriceId = newAddons[currentAddonIndex]?.stripeId ?? null;

      set({ yearly, expertAddonPriceId });
    },
    setExpertAddonPriceId: (priceId): void => {
      set({ expertAddonPriceId: priceId });
    },
    setMembersQuantity: (quantity: number): void => {
      set({ quantity });
    },
    setUpgrade: (upgrade): void => {
      set({ upgrade });

      const { subscription } = useSubscriptionStore.getState();

      if (upgrade && subscription?.plan) {
        set({
          plan: getUpgradePlan(subscription.plan.name),
        });
      }
    },
    setUpgradeToExpertPlan: (upgradeToExpertPlan): void => {
      set({
        upgradeToExpertPlan,
        plan: upgradeToExpertPlan
          ? SubscriptionPlanType.EXPERT
          : SubscriptionPlanType.PRO,
      });
    },
    setPromoCode: (promoCode): void => {
      set({ promoCode });
    },
    setSelectionDisabled: (disabled: boolean): void => {
      set({ disabled });
    },
    setFromSubscription: (subscription): void => {
      if (!subscription.plan) {
        return;
      }

      set({
        plan: subscription.plan.name,
        yearly:
          'interval' in subscription.plan &&
          subscription.plan.interval === 'year',
        expertAddonPriceId:
          'addon' in subscription.plan
            ? ((subscription.plan.addon?.priceId ??
                null) as StripePriceId | null)
            : null,
        quantity:
          'quantity' in subscription.plan ? subscription.plan.quantity : 1,
        upgrade: false,
        promoCode: null,
      });
    },
  },
});
